/**
 * Rules API client — thin wrappers over /api/v1/rules.
 *
 * Reuses the shared axios instance configured in ./api by piggy-backing
 * on the same VITE_API_BASE_URL + Firebase auth interceptor, but lives in
 * a separate file so PRP-002 doesn't have to touch api.ts (which a
 * parallel session is editing).
 */

import axios from 'axios';
import { auth } from './firebase';
import type {
  ExplainRuleResponse,
  RuleDetail,
  RuleListItem,
  SaveRuleRequest,
  SaveRuleResponse,
  TranslateRuleRequest,
  TranslateRuleResponse,
} from '../types/rules';

const BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'https://est.anterotrail.com/api/v1';

const client = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

client.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  if (user) {
    try {
      const token = await user.getIdToken();
      config.headers.Authorization = `Bearer ${token}`;
    } catch (err) {
      console.error('[rulesApi] could not get firebase token', err);
    }
  } else if (
    import.meta.env.DEV &&
    import.meta.env.VITE_DEV_AUTH_BYPASS === 'true'
  ) {
    config.headers.Authorization = 'Bearer dev-bypass-token';
  }
  return config;
});

client.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const message =
      error?.response?.data?.detail ||
      error?.response?.data?.message ||
      error?.message ||
      'Failed to load rules';
    return Promise.reject(new Error(message));
  },
);

export interface RuleUpdatePayload {
  name?: string;
  description?: string;
  category?: string;
  priority?: number;
  conditions?: Record<string, unknown>;
  actions?: Array<Record<string, unknown>>;
  citations?: Array<Record<string, unknown>>;
  produces_facts?: string[];
  jurisdictions?: string[];
  damage_types?: string[];
  is_active?: boolean;
  rule_type?: string;
  status?: string;
  severity?: string;
  domains?: string[];
  tags?: string[];
  change_summary?: string;
}

export const RulesAPI = {
  list: (): Promise<RuleListItem[]> => client.get('/rules/'),
  get: (id: string): Promise<RuleDetail> =>
    client.get(`/rules/${encodeURIComponent(id)}`),

  // PRP-004 — structured editor / versioning.
  update: (id: string, payload: RuleUpdatePayload): Promise<RuleDetail> =>
    client.put(`/rules/${encodeURIComponent(id)}`, payload),

  // PRP-011 — LLM-assisted authoring.
  translateFromNaturalLanguage: (
    body: TranslateRuleRequest,
  ): Promise<TranslateRuleResponse> =>
    client.post('/rules/from_natural_language', body),
  saveWithRender: (body: SaveRuleRequest): Promise<SaveRuleResponse> =>
    client.post('/rules/save_with_render', body),
  explain: (id: string, forceRefresh = false): Promise<ExplainRuleResponse> =>
    client.post(
      `/rules/${encodeURIComponent(id)}/explain${
        forceRefresh ? '?force_refresh=true' : ''
      }`,
    ),
};
